import React, { useState } from 'react';
import { Send } from 'lucide-react';
import toast from 'react-hot-toast';
import { supabase } from '../lib/supabase';
import { motion } from 'framer-motion';
import AnimatedSection from './AnimatedSection';
import FormInput from './FormInput';
import LoadingButton from './LoadingButton';

const Contact: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    zip_code: '',
    referral_source: '',
    message: ''
  });
  const [website, setWebsite] = useState('');

  const [formErrors, setFormErrors] = useState({
    name: '',
    email: '',
    phone: '',
    zip_code: '',
    referral_source: '',
    message: ''
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  
  const validateForm = () => {
    let valid = true;
    const newErrors = {
      name: '',
      email: '',
      phone: '',
      zip_code: '',
      referral_source: '',
      message: ''
    };
    
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
      valid = false;
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
      valid = false;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
      valid = false;
    }
    
    // Phone validation (required)
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
      valid = false;
    } else if (!/^[\d\s\-()+.]{10,20}$/.test(formData.phone.trim())) {
      newErrors.phone = 'Please enter a valid phone number';
      valid = false;
    }
    
    // Zip code validation (required)
    if (!formData.zip_code.trim()) {
      newErrors.zip_code = 'Zip code is required';
      valid = false;
    } else if (!/^\d{5}(-\d{4})?$/.test(formData.zip_code.trim())) {
      newErrors.zip_code = 'Please enter a valid zip code (e.g., 12345 or 12345-6789)';
      valid = false;
    }

    if (!formData.message.trim()) {
      newErrors.message = 'Message is required';
      valid = false;
    }
    
    setFormErrors(newErrors);
    return valid;
  };
  
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (website) {
      setSubmitSuccess(true);
      setFormData({ name: '', email: '', phone: '', zip_code: '', referral_source: '', message: '' });
      toast.success('Message received! We\'ll get back to you soon.');
      setTimeout(() => setSubmitSuccess(false), 5000);
      return;
    }

    if (validateForm()) {
      setIsSubmitting(true);
      
      try {
        // Save to database
        const { error: dbError } = await supabase
          .from('contact_messages')
          .insert([{
            name: formData.name,
            email: formData.email,
            phone: formData.phone.trim(),
            zip_code: formData.zip_code.trim(),
            referral_source: formData.referral_source.trim(),
            subject: null,
            message: formData.message
          }]);

        if (dbError) {
          console.error('Database error:', dbError);
          throw new Error('Failed to save your message. Please try again later.');
        }

        // Attempt to send email notification (will fail silently if edge function not deployed)
        try {
          const functionUrl = 'https://tbohpoppqpbshoebvyzy.supabase.co/functions/v1/send-contact-email';
          console.log('Attempting to call edge function:', functionUrl);
          
          const emailResponse = await fetch(functionUrl, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              name: formData.name,
              email: formData.email,
              phone: formData.phone,
              zip_code: formData.zip_code,
              referral_source: formData.referral_source,
              message: formData.message
            })
          });

          console.log('Edge function response status:', emailResponse.status);
          
          if (emailResponse.ok) {
            const responseData = await emailResponse.json();
            console.log('Email notification sent successfully:', responseData);
            console.log('Email notification sent successfully');
          } else {
            const errorData = await emailResponse.text();
            console.error('Email notification failed:', emailResponse.status, errorData);
            console.log('Email notification failed, but message was saved');
          }
        } catch (emailError) {
          console.error('Email notification failed:', emailError);
          console.log('Email notification failed, but message was saved');
        }
        
        setSubmitSuccess(true);
        setFormData({
          name: '',
          email: '',
          phone: '',
          zip_code: '',
          referral_source: '',
          message: ''
        });
        
        toast.success('Message received! We\'ll get back to you soon.');
        
        setTimeout(() => {
          setSubmitSuccess(false);
        }, 5000);
      } catch (error) {
        console.error('Error submitting form:', error);
        toast.error(error.message || 'Failed to send message. Please try again later.');
      } finally {
        setIsSubmitting(false);
      }
    }
  };
  
  return (
    <section id="contact" className="py-20 bg-white">
      <div className="container mx-auto px-4">
        <AnimatedSection className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Join Us</h2>
          <div className="w-20 h-1 bg-blue-600 mx-auto mb-6"></div>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Have questions about Monday Night Group? Want to join our next meeting? 
            We'd love to hear from you! Get in touch using the form below.
          </p>
        </AnimatedSection>
        
        <div className="max-w-2xl mx-auto">
          <motion.form 
            onSubmit={handleSubmit} 
            className="bg-gray-50 p-8 rounded-lg shadow-lg"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            {submitSuccess && (
              <motion.div 
                className="mb-6 bg-green-50 border border-green-100 text-green-700 px-4 py-3 rounded"
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
              >
                <p>Your message has been received! We'll review it soon.</p>
              </motion.div>
            )}
            
            <h3 className="text-2xl font-bold text-gray-800 mb-6">Get In Touch</h3>

            <div aria-hidden="true" style={{ position: 'absolute', left: '-9999px', top: '-9999px', height: 0, width: 0, overflow: 'hidden' }}>
              <label htmlFor="website">Website</label>
              <input
                type="text"
                id="website"
                name="website"
                value={website}
                onChange={(e) => setWebsite(e.target.value)}
                tabIndex={-1}
                autoComplete="off"
              />
            </div>
            
            <FormInput
              label="Name*"
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              error={formErrors.name}
              touched={true}
              placeholder="Your name"
              maxLength={100}
            />
            
            <FormInput
              label="Email*"
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              error={formErrors.email}
              touched={true}
              placeholder="Your email address"
              maxLength={255}
            />
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormInput
                label="Phone Number*"
                type="tel"
                name="phone"
                required
                value={formData.phone}
                onChange={handleChange}
                error={formErrors.phone}
                touched={true}
                placeholder="(555) 123-4567"
                maxLength={20}
              />

              <FormInput
                label="Zip Code*"
                type="text"
                name="zip_code"
                required
                value={formData.zip_code}
                onChange={handleChange}
                error={formErrors.zip_code}
                touched={true}
                placeholder="12345"
                maxLength={10}
              />
            </div>

            <FormInput
              label="Who invited you — or how did you hear about this group?"
              type="text"
              name="referral_source"
              value={formData.referral_source}
              onChange={handleChange}
              error={formErrors.referral_source}
              touched={true}
              placeholder="Friend, social media, web search, etc."
              maxLength={255}
            />

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">Message*</label>
              <textarea
                name="message"
                value={formData.message}
                onChange={handleChange}
                rows={5}
                className={`w-full px-4 py-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 transition ${
                  formErrors.message ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Your message"
                maxLength={5000}
              ></textarea>
              {formErrors.message && (
                <p className="mt-1 text-sm text-red-600">{formErrors.message}</p>
              )}
            </div>
            
            <LoadingButton
              type="submit"
              loading={isSubmitting}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 px-6 rounded-md font-medium transition-all duration-300"
            >
              <span className="flex items-center justify-center">
                Send Message
                <Send className="ml-2 h-5 w-5" />
              </span>
            </LoadingButton>
          </motion.form>
        </div>
      </div>
    </section>
  );
};

export default Contact;